/**
 * The prompt a tutor copies into ChatGPT to have it write a practice in
 * Athena's Markdown format.
 *
 * One string, in one place, because it is documentation of a format the server
 * owns. The rules below are the ones `packages/markdown` actually enforces
 * — the parser for the shapes (`- A. text`, `- left -> right`, `**Type:**`,
 * `**Answer:**`, `**Explanation:**`), the validator for the counts and the
 * answer rules, and `LIMITS` in `@athena/shared` for the numbers.
 *
 * There is exactly one other description of this format:
 * `apps/api/src/generation/prompt.ts`, which is what Athena's own API sends a
 * model on the Automatic tab. The two exist separately because they address
 * different readers — this one a tutor's own ChatGPT session, that one a
 * server-side call with source material attached — but they describe the same
 * parser, so a change to the format is a change to both. Neither is the
 * canonical copy; `packages/markdown` is, and these two follow it.
 *
 * Deliberately a constant rather than a builder. A tutor fills in the subject
 * and topic themselves, in ChatGPT, so there is nothing here for the app to
 * interpolate.
 *
 * No backticks: the prompt tells ChatGPT not to wrap its answer in a code
 * fence, and a prompt that contained fences of its own would be arguing with
 * itself.
 *
 * ── Deliberately not translated ──────────────────────────────────────────
 *
 * The panel around it is (see `practice.chatgpt.*`); this string is not, and
 * that is a decision rather than an oversight. It is a specification of the
 * Markdown format, addressed to a language model, and the format is
 * English-keyed: `packages/markdown/src/parser` matches on `**Answer:**`,
 * `**Type:**` and `**Explanation:**` literally. A Spanish translation of this
 * prompt would have ChatGPT emit `**Respuesta:**`, and the server would reject
 * the document the tutor pasted back — a worse outcome than an English prompt.
 *
 * A tutor writes their instructions to ChatGPT in their own language anyway:
 * they paste this and then say what the practice should be about, in whatever
 * language they like. The model reads both.
 *
 * Localizing the Markdown format itself is the V2 question this defers, and it
 * is a bigger one than this string — it is the parser, the validator, and
 * every practice already authored. See .claude/rules/localization.md.
 */
export const PRACTICE_CHATGPT_PROMPT = `You are writing an educational practice for Athena.

Return ONLY the practice, as plain Markdown in the Athena format described below. Do not add an introduction, a summary, commentary, or a code fence around it.

Write the practice about the subject and topic I give you at the end of this message, and use a variety of the supported question types wherever they suit the material.

Supported question types:

1. Multiple Choice
2. Fill
3. Association (put items in order)
4. Matching (pair items up)

=== PRACTICE STRUCTURE ===

# Practice Title

## Question 1

...

## Question 2

...

Rules:

- Exactly one "# " heading, at the very top. It is the name of the practice and should describe the topic.
- Every question begins with a "## Question N" heading, numbered from 1 with no gaps.
- The question text goes on its own line directly under its heading, before any list lines or field lines.
- The field lines are written exactly as "**Type:** ...", "**Answer:** ..." and "**Explanation:** ...", each on a line of its own.
- Leave a blank line between blocks.
- A practice can have at most 200 questions. Five to ten is a normal size.
- Never begin a line of question text with "**". A line starting that way is read as a field line and is lost.

=== FORMATTING AND MATHEMATICS ===

Question text, options, matching pairs and explanations are shown as Markdown with mathematics:

- Use "**bold**" and "*italic*" where they help.
- Write mathematics in LaTeX, inline between single dollar signs: "What is the expansion of $(a - b)^2$?". For a formula on its own line, use double dollar signs on the lines above and below it.
- Use LaTeX for exponents, fractions, roots, subscripts and symbols: "$x^2$" rather than "x^2", "$\\frac{1}{2}$" rather than "1/2".
- An option and one side of a matching pair are single lines, so any mathematics in them must be inline.
- Escape a literal dollar sign as "\\$", or it is read as the start of a formula: "Ana has \\$5 and Beto has \\$10".

=== 1. MULTIPLE CHOICE ===

## Question 1

Which process turns liquid water into water vapour?

- A. Evaporation
- B. Condensation
- C. Precipitation
- D. Collection

**Answer:** A

**Explanation:** Heat from the sun turns liquid water into water vapour.

Rules:

- Options are written "- A. text", lettered A, B, C, D... in order. Between 2 and 6 options; four is a good default.
- Exactly one option is correct.
- "**Answer:**" is a single option letter.
- "**Explanation:**" is required.
- No "**Type:**" line is needed. Two options reading exactly "True" and "False" become a true/false question.

=== 2. FILL ===

## Question 2

Water that falls from clouds as rain, snow or hail is called __________.

**Answer:** precipitation

**Explanation:** Precipitation is water that falls from clouds to the ground.

Rules:

- A Fill question has no option list. Do not write "- A. ..." lines for it.
- "**Answer:**" lists every accepted answer, separated by "|".
- Answers are compared after trimming, collapsing repeated spaces, lowercasing, and dropping one trailing full stop. So do not list capitalisation or spacing variants — list only genuinely different acceptable answers.
- Write the prompt as a sentence with a blank, "__________", where the answer goes. The answer must be the one piece of wording the sentence needs, so that every student who knows it types the same thing.
- Every accepted answer is 1 to 3 words: a word, a name or a short noun phrase, such as "satellite", "the house", "Central Market" or "San José". An answer longer than 3 words is rejected. Never ask a Fill question whose answer is a sentence, a definition or an explanation — students word those differently and would be marked wrong. When the material only supports a longer answer, use a multiple-choice question instead.
- "**Explanation:**" is required.

=== 3. ASSOCIATION ===

Association is an ordering question: the student puts the options into the correct sequence. It is not Matching.

## Question 3

Put the four stages of the water cycle in the order they happen.

- A. Water collects in rivers and oceans
- B. Water evaporates into the air
- C. Water vapour condenses into clouds
- D. Precipitation falls to the ground

**Type:** Association

**Answer:** B, C, D, A

**Explanation:** Water evaporates, condenses into clouds, falls as precipitation, and collects again.

Rules:

- Write "**Type:** Association". Without that line the question is read as multiple choice.
- The options are "- A. text" lines, between 2 and 6 of them, each one item or step to be placed in the sequence.
- List the options in a scrambled order, not already in the correct one.
- No two options may read the same.
- "**Answer:**" lists every option letter exactly once, separated by commas, in the correct order.
- "**Explanation:**" is required.

=== 4. MATCHING ===

Matching pairs each left-hand item with the one right-hand item that goes with it. It is not an ordering question.

## Question 4

Match each stage of the water cycle with what happens during it.

**Type:** Matching

- Evaporation -> Liquid water turns into water vapour
- Condensation -> Water vapour cools and forms clouds
- Precipitation -> Water falls from the clouds to the ground
- Collection -> Water gathers in rivers, lakes and oceans

**Explanation:** Each stage moves water along to the next part of the cycle.

Rules:

- Write "**Type:** Matching".
- Each pair is one line, "- left item -> right item". Between 2 and 8 pairs.
- Each left item matches exactly one right item.
- Every left item must be different from the others, and every right item must be different from the others.
- Do not use option letters for a Matching question.
- Do not write an "**Answer:**" line for a Matching question: the pairs themselves are the answer.
- Do not use "->" anywhere else in the question, including in the question text.
- Do not use correctOrder or submittedOrder.
- "**Explanation:**" is required.

=== GENERATION RULES ===

- Follow the Athena Markdown syntax above exactly.
- Return Markdown only.
- Use only the four supported question types.
- Include an "**Answer:**" line for every question except Matching.
- Include an "**Explanation:**" line for every question.
- Use "**Type:** Association" only for Association questions.
- Use "**Type:** Matching" only for Matching questions.
- Do not add fields the format does not describe.
- Do not add HTML, JSON, or YAML.
- Do not add any code or text outside the practice itself.
- Do not wrap the practice in triple backticks.
- Do not put an answer key anywhere outside the question blocks.
- Make sure every answer really answers its own question.
- Make sure the Association order is genuinely correct.
- Make sure the Matching pairs are genuinely correct.
- Make sure Fill answers are ones a student could reasonably write, separated by "|" when more than one is acceptable.

=== A COMPLETE, VALID EXAMPLE ===

# The Water Cycle

## Question 1

Which process turns liquid water into water vapour?

- A. Evaporation
- B. Condensation
- C. Precipitation
- D. Collection

**Answer:** A

**Explanation:** Heat from the sun turns liquid water into vapour, which rises into the air.

## Question 2

What is the name for water that falls from clouds as rain, snow or hail?

**Answer:** precipitation | rain

**Explanation:** Precipitation is any water that falls from the clouds back to the ground.

## Question 3

Put the four stages of the water cycle in the order they happen.

- A. Water collects in rivers and oceans
- B. Water evaporates into the air
- C. Water vapour condenses into clouds
- D. Precipitation falls to the ground

**Type:** Association

**Answer:** B, C, D, A

**Explanation:** Water evaporates, condenses into clouds, falls as precipitation, and collects again.

## Question 4

Match each stage of the water cycle with what happens during it.

**Type:** Matching

- Evaporation -> Liquid water turns into water vapour
- Condensation -> Water vapour cools and forms clouds
- Precipitation -> Water falls from the clouds to the ground
- Collection -> Water gathers in rivers, lakes and oceans

**Explanation:** Each stage moves water along to the next part of the cycle.
`
